import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  Calculator,
  Euro,
  Hammer,
  Layers3,
  Package,
  Plus,
  Receipt,
  Trash2,
  Building2,
  BriefcaseBusiness,
  UserRound,
} from "lucide-react";

import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import UIButton from "../components/UIButton";
import UIInput from "../components/UIInput";
import UILabel from "../components/UILabel";
import UISelect from "../components/UISelect";
import { notify } from "../utils/notify";

const createId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const makeMaterialRow = () => ({
  id: createId("mat"),
  sourceId: "",
  name: "",
  quantity: 1,
  unit: "pcs",
  unitPrice: 0,
});

const makeLaborRow = () => ({
  id: createId("lab"),
  workerId: "",
  role: "",
  workers: 1,
  days: 1,
  hoursPerDay: 8,
  hourlyRate: 0,
});

const makeExtraRow = () => ({
  id: createId("ext"),
  title: "",
  amount: 0,
});

const createInitialForm = () => ({
  title: "",
  projectId: "",
  projectName: "",
  clientId: "",
  clientName: "",
  address: "",
  areaM2: "",
  notes: "",
  overheadPercent: 10,
  profitPercent: 20,
  vatPercent: 19,
  materials: [makeMaterialRow()],
  labor: [makeLaborRow()],
  extras: [makeExtraRow()],
});

const ui = {
  sq: {
    pageTitle: "Kalkulation",
    pageSubtitle:
      "Përllogarit materialet, punën dhe shpenzimet për me nxjerrë çmimin final të projektit.",
    basicInfo: "Të dhënat bazë",
    basicInfoSubtitle: "Zgjedh projektin, klientin dhe parametrat kryesorë.",
    materials: "Materialet",
    labor: "Puna / orët",
    extras: "Shpenzime shtesë",
    summary: "Përmbledhja",
    savedList: "Kalkulimet e ruajtura",
    title: "Titulli i kalkulimit",
    titlePlaceholder: "P.sh. Renovim banjo - Krasniqi",
    project: "Projekti",
    chooseProject: "Zgjedh projektin",
    projectName: "Emri i projektit",
    client: "Klienti",
    chooseClient: "Zgjedh klientin",
    clientName: "Emri i klientit",
    address: "Adresa",
    areaM2: "Sipërfaqja m²",
    notes: "Shënime",
    notesPlaceholder: "Shkruaj kërkesat e klientit, fazat e punës ose komente shtesë...",
    overheadPercent: "Overhead %",
    profitPercent: "Fitimi %",
    vatPercent: "TVSH %",
    materialsCost: "Kosto materiale",
    laborCost: "Kosto pune",
    extrasCost: "Kosto shtesë",
    finalPrice: "Çmimi final",
    materialName: "Materiali",
    quantity: "Sasia",
    unit: "Njësia",
    unitPrice: "Çmimi / njësi",
    total: "Totali",
    addMaterial: "Shto material",
    role: "Roli / përshkrimi",
    workerTemplate: "Punëtor / template",
    chooseWorker: "Zgjedh punëtorin",
    workers: "Nr. punëtorëve",
    days: "Ditë",
    hoursPerDay: "Orë / ditë",
    hourlyRate: "Çmimi / orë",
    addLabor: "Shto punë",
    extraTitle: "Përshkrimi",
    amount: "Shuma",
    addExtra: "Shto shpenzim",
    materialsSubtotal: "Totali materialeve",
    laborSubtotal: "Totali punës",
    extrasSubtotal: "Totali shtesave",
    subtotal: "Nëntotali",
    overheadAmount: "Overhead",
    profitAmount: "Fitimi",
    netOffer: "Çmimi pa TVSH",
    vatAmount: "TVSH",
    grandTotal: "Çmimi final",
    pricePerM2: "Çmimi / m²",
    save: "Ruaj kalkulimin",
    saving: "Duke ruajtur...",
    requiredTitle: "Shkruaje titullin e kalkulimit.",
    saveSuccess: "Kalkulimi u ruajt me sukses.",
    saveError: "Gabim gjatë ruajtjes së kalkulimit.",
    loadError: "Gabim gjatë leximit të të dhënave.",
    noSaved: "Ende nuk ka kalkulime të ruajtura.",
    createdAt: "Krijuar",
    remove: "Hiqe",
    custom: "Manual",
    chooseMaterial: "Zgjedh material",
    materialTemplate: "Material / template",
    noProjectName: "Pa emër projekti",
    noClientName: "Pa klient",
  },
  de: {
    pageTitle: "Kalkulation",
    pageSubtitle:
      "Berechne Material, Arbeit und Zusatzkosten, um den finalen Projektpreis sauber zu ermitteln.",
    basicInfo: "Grunddaten",
    basicInfoSubtitle: "Projekt, Kunde und Hauptparameter auswählen.",
    materials: "Materialien",
    labor: "Arbeit / Stunden",
    extras: "Zusatzkosten",
    summary: "Zusammenfassung",
    savedList: "Gespeicherte Kalkulationen",
    title: "Titel der Kalkulation",
    titlePlaceholder: "Z. B. Badsanierung - Krasniqi",
    project: "Projekt",
    chooseProject: "Projekt auswählen",
    projectName: "Projektname",
    client: "Kunde",
    chooseClient: "Kunden auswählen",
    clientName: "Kundenname",
    address: "Adresse",
    areaM2: "Fläche m²",
    notes: "Notizen",
    notesPlaceholder: "Kundenwünsche, Arbeitsphasen oder zusätzliche Kommentare...",
    overheadPercent: "Overhead %",
    profitPercent: "Gewinn %",
    vatPercent: "MwSt %",
    materialsCost: "Materialkosten",
    laborCost: "Arbeitskosten",
    extrasCost: "Zusatzkosten",
    finalPrice: "Endpreis",
    materialName: "Material",
    quantity: "Menge",
    unit: "Einheit",
    unitPrice: "Preis / Einheit",
    total: "Gesamt",
    addMaterial: "Material hinzufügen",
    role: "Rolle / Beschreibung",
    workerTemplate: "Mitarbeiter / Vorlage",
    chooseWorker: "Mitarbeiter wählen",
    workers: "Anzahl Mitarbeiter",
    days: "Tage",
    hoursPerDay: "Std / Tag",
    hourlyRate: "Preis / Stunde",
    addLabor: "Arbeit hinzufügen",
    extraTitle: "Beschreibung",
    amount: "Betrag",
    addExtra: "Kosten hinzufügen",
    materialsSubtotal: "Materialsumme",
    laborSubtotal: "Arbeitssumme",
    extrasSubtotal: "Zusatzsumme",
    subtotal: "Zwischensumme",
    overheadAmount: "Overhead",
    profitAmount: "Gewinn",
    netOffer: "Preis ohne MwSt",
    vatAmount: "MwSt",
    grandTotal: "Endpreis",
    pricePerM2: "Preis / m²",
    save: "Kalkulation speichern",
    saving: "Wird gespeichert...",
    requiredTitle: "Bitte einen Titel eingeben.",
    saveSuccess: "Kalkulation wurde gespeichert.",
    saveError: "Fehler beim Speichern der Kalkulation.",
    loadError: "Fehler beim Laden der Daten.",
    noSaved: "Noch keine Kalkulationen gespeichert.",
    createdAt: "Erstellt",
    remove: "Entfernen",
    custom: "Manuell",
    chooseMaterial: "Material auswählen",
    materialTemplate: "Material / Vorlage",
    noProjectName: "Ohne Projektname",
    noClientName: "Ohne Kunde",
  },
};

function n(value) {
  return Number(value || 0);
}

function formatMoney(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(n(value));
}

function formatCreatedAt(value) {
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
  if (value instanceof Date) return value.toLocaleString();
  return "-";
}

export default function Kalkulation({ lang = "sq" }) {
  const { theme, currentTheme } = usePanelTheme();
  const text = ui[lang] || ui.sq;

  const [form, setForm] = useState(createInitialForm());
  const [saving, setSaving] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [materialsCatalog, setMaterialsCatalog] = useState([]);
  const [workersCatalog, setWorkersCatalog] = useState([]);

  const textareaClass = `min-h-[110px] w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:bg-white"
  }`;

  const statItems = useMemo(() => {
    const materialsSubtotal = form.materials.reduce(
      (sum, item) => sum + n(item.quantity) * n(item.unitPrice),
      0
    );
    const laborSubtotal = form.labor.reduce(
      (sum, item) => sum + n(item.workers) * n(item.days) * n(item.hoursPerDay) * n(item.hourlyRate),
      0
    );
    const extrasSubtotal = form.extras.reduce((sum, item) => sum + n(item.amount), 0);
    const subtotal = materialsSubtotal + laborSubtotal + extrasSubtotal;
    const overheadAmount = subtotal * (n(form.overheadPercent) / 100);
    const baseWithOverhead = subtotal + overheadAmount;
    const profitAmount = baseWithOverhead * (n(form.profitPercent) / 100);
    const netOffer = baseWithOverhead + profitAmount;
    const vatAmount = netOffer * (n(form.vatPercent) / 100);
    const grandTotal = netOffer + vatAmount;
    const pricePerM2 = n(form.areaM2) > 0 ? grandTotal / n(form.areaM2) : 0;

    return {
      materialsSubtotal,
      laborSubtotal,
      extrasSubtotal,
      subtotal,
      overheadAmount,
      profitAmount,
      netOffer,
      vatAmount,
      grandTotal,
      pricePerM2,
    };
  }, [form]);

  const topStats = [
    { label: text.materialsCost, value: statItems.materialsSubtotal, icon: Package },
    { label: text.laborCost, value: statItems.laborSubtotal, icon: Hammer },
    { label: text.extrasCost, value: statItems.extrasSubtotal, icon: Receipt },
    { label: text.finalPrice, value: statItems.grandTotal, icon: Euro },
  ];

  const loadAll = async () => {
    try {
      const [calculationsSnap, projectsSnap, clientsSnap, materialsSnap, workersSnap] =
        await Promise.all([
          getDocs(query(collection(db, "calculations"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "invoice_clients"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "materials"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "workers"), orderBy("createdAt", "desc"))),
        ]);

      setSavedCalculations(calculationsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProjects(projectsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setClients(clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMaterialsCatalog(materialsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setWorkersCatalog(workersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error(error);
      notify.error(text.loadError);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProjectSelect = (projectId) => {
    const selected = projects.find((item) => item.id === projectId);
    setForm((prev) => ({
      ...prev,
      projectId,
      projectName: selected?.name || prev.projectName,
      address: selected?.address || prev.address,
      areaM2: selected?.areaM2 || prev.areaM2,
      clientName: !prev.clientId && selected?.clientName ? selected.clientName : prev.clientName,
    }));
  };

  const handleClientSelect = (clientId) => {
    const selected = clients.find((item) => item.id === clientId);
    setForm((prev) => ({
      ...prev,
      clientId,
      clientName:
        selected?.companyName || selected?.name || prev.clientName,
      address: prev.address || selected?.address || "",
    }));
  };

  const updateMaterial = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      materials: prev.materials.map((item) => {
        if (item.id !== id) return item;
        let next = { ...item, [field]: value };
        if (field === "sourceId") {
          const selected = materialsCatalog.find((mat) => mat.id === value);
          next = {
            ...next,
            sourceId: value,
            name: selected?.name || "",
            unit: selected?.unit || item.unit || "pcs",
            unitPrice: selected?.price || 0,
          };
        }
        return next;
      }),
    }));
  };

  const updateLabor = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      labor: prev.labor.map((item) => {
        if (item.id !== id) return item;
        let next = { ...item, [field]: value };
        if (field === "workerId") {
          const selected = workersCatalog.find((worker) => worker.id === value);
          const dailyRate = n(selected?.dailyRate);
          next = {
            ...next,
            workerId: value,
            role: selected?.role || selected?.name || "",
            hourlyRate: dailyRate > 0 ? Number((dailyRate / 8).toFixed(2)) : 0,
          };
        }
        return next;
      }),
    }));
  };

  const updateExtra = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      extras: prev.extras.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    }));
  };

  const handleSave = async () => {
    if (!String(form.title || "").trim()) {
      notify.error(text.requiredTitle);
      return;
    }

    const payload = {
      ...form,
      areaM2: n(form.areaM2),
      overheadPercent: n(form.overheadPercent),
      profitPercent: n(form.profitPercent),
      vatPercent: n(form.vatPercent),
      materials: form.materials.map((item) => ({
        ...item,
        quantity: n(item.quantity),
        unitPrice: n(item.unitPrice),
        total: n(item.quantity) * n(item.unitPrice),
      })),
      labor: form.labor.map((item) => ({
        ...item,
        workers: n(item.workers),
        days: n(item.days),
        hoursPerDay: n(item.hoursPerDay),
        hourlyRate: n(item.hourlyRate),
        total: n(item.workers) * n(item.days) * n(item.hoursPerDay) * n(item.hourlyRate),
      })),
      extras: form.extras.map((item) => ({
        ...item,
        amount: n(item.amount),
      })),
      totals: statItems,
      createdAt: serverTimestamp(),
    };

    try {
      setSaving(true);
      await addDoc(collection(db, "calculations"), payload);
      notify.success(text.saveSuccess);
      setForm(createInitialForm());
      await loadAll();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell>
      <SectionHeading title={text.pageTitle} subtitle={text.pageSubtitle} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {topStats.map((item) => {
          const Icon = item.icon;
          return (
            <PanelCard key={item.label} className="p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className={`text-sm font-medium ${currentTheme.muted}`}>{item.label}</p>
                  <h3 className={`mt-2 text-2xl font-black ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    {formatMoney(item.value)}
                  </h3>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600">
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </PanelCard>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.5fr_0.9fr]">
        <div className="space-y-6">
          <PanelCard>
            <SectionHeading title={text.basicInfo} subtitle={text.basicInfoSubtitle} />
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <UILabel>{text.title}</UILabel>
                <UIInput value={form.title} onChange={(e) => updateField("title", e.target.value)} placeholder={text.titlePlaceholder} />
              </div>

              <div>
                <UILabel>{text.project}</UILabel>
                <UISelect value={form.projectId} onChange={(e) => handleProjectSelect(e.target.value)}>
                  <option value="">{text.chooseProject}</option>
                  {projects.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || text.noProjectName}
                    </option>
                  ))}
                </UISelect>
              </div>

              <div>
                <UILabel>{text.projectName}</UILabel>
                <UIInput value={form.projectName} onChange={(e) => updateField("projectName", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.client}</UILabel>
                <UISelect value={form.clientId} onChange={(e) => handleClientSelect(e.target.value)}>
                  <option value="">{text.chooseClient}</option>
                  {clients.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.companyName || item.name || text.noClientName}
                    </option>
                  ))}
                </UISelect>
              </div>

              <div>
                <UILabel>{text.clientName}</UILabel>
                <UIInput value={form.clientName} onChange={(e) => updateField("clientName", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.address}</UILabel>
                <UIInput value={form.address} onChange={(e) => updateField("address", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.areaM2}</UILabel>
                <UIInput type="number" value={form.areaM2} onChange={(e) => updateField("areaM2", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.overheadPercent}</UILabel>
                <UIInput type="number" value={form.overheadPercent} onChange={(e) => updateField("overheadPercent", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.profitPercent}</UILabel>
                <UIInput type="number" value={form.profitPercent} onChange={(e) => updateField("profitPercent", e.target.value)} />
              </div>

              <div>
                <UILabel>{text.vatPercent}</UILabel>
                <UIInput type="number" value={form.vatPercent} onChange={(e) => updateField("vatPercent", e.target.value)} />
              </div>

              <div className="md:col-span-2">
                <UILabel>{text.notes}</UILabel>
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder={text.notesPlaceholder}
                  className={textareaClass}
                />
              </div>
            </div>
          </PanelCard>

          <PanelCard>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <SectionHeading title={text.materials} subtitle={text.materialsSubtotal} />
              <UIButton type="button" theme={theme} onClick={() => setForm((prev) => ({ ...prev, materials: [...prev.materials, makeMaterialRow()] }))} className="gap-2 self-start md:self-auto">
                <Plus className="h-4 w-4" />
                {text.addMaterial}
              </UIButton>
            </div>

            <div className="space-y-4">
              {form.materials.map((item) => {
                const lineTotal = n(item.quantity) * n(item.unitPrice);
                return (
                  <div key={item.id} className={`grid gap-3 rounded-2xl border p-4 md:grid-cols-12 ${theme === "dark" ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                    <div className="md:col-span-3">
                      <UILabel>{text.materialTemplate}</UILabel>
                      <UISelect value={item.sourceId} onChange={(e) => updateMaterial(item.id, "sourceId", e.target.value)}>
                        <option value="">{text.custom}</option>
                        {materialsCatalog.map((mat) => (
                          <option key={mat.id} value={mat.id}>{mat.name || text.chooseMaterial}</option>
                        ))}
                      </UISelect>
                    </div>
                    <div className="md:col-span-3">
                      <UILabel>{text.materialName}</UILabel>
                      <UIInput value={item.name} onChange={(e) => updateMaterial(item.id, "name", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <UILabel>{text.quantity}</UILabel>
                      <UIInput type="number" value={item.quantity} onChange={(e) => updateMaterial(item.id, "quantity", e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                      <UILabel>{text.unit}</UILabel>
                      <UIInput value={item.unit} onChange={(e) => updateMaterial(item.id, "unit", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <UILabel>{text.unitPrice}</UILabel>
                      <UIInput type="number" value={item.unitPrice} onChange={(e) => updateMaterial(item.id, "unitPrice", e.target.value)} />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <UIButton type="button" theme={theme} variant="softDanger" size="icon" onClick={() => setForm((prev) => ({ ...prev, materials: prev.materials.length > 1 ? prev.materials.filter((row) => row.id !== item.id) : prev.materials }))} title={text.remove}>
                        <Trash2 className="h-4 w-4" />
                      </UIButton>
                    </div>
                    <div className="md:col-span-12">
                      <div className={`rounded-2xl border border-dashed px-4 py-3 text-sm font-bold ${theme === "dark" ? "border-slate-700 text-slate-200" : "border-slate-300 text-slate-700"}`}>
                        {text.total}: {formatMoney(lineTotal)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </PanelCard>

          <PanelCard>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <SectionHeading title={text.labor} subtitle={text.laborSubtotal} />
              <UIButton type="button" theme={theme} onClick={() => setForm((prev) => ({ ...prev, labor: [...prev.labor, makeLaborRow()] }))} className="gap-2 self-start md:self-auto">
                <Plus className="h-4 w-4" />
                {text.addLabor}
              </UIButton>
            </div>

            <div className="space-y-4">
              {form.labor.map((item) => {
                const lineTotal = n(item.workers) * n(item.days) * n(item.hoursPerDay) * n(item.hourlyRate);
                return (
                  <div key={item.id} className={`grid gap-3 rounded-2xl border p-4 md:grid-cols-12 ${theme === "dark" ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                    <div className="md:col-span-3">
                      <UILabel>{text.workerTemplate}</UILabel>
                      <UISelect value={item.workerId} onChange={(e) => updateLabor(item.id, "workerId", e.target.value)}>
                        <option value="">{text.custom}</option>
                        {workersCatalog.map((worker) => (
                          <option key={worker.id} value={worker.id}>{worker.name || worker.role || text.chooseWorker}</option>
                        ))}
                      </UISelect>
                    </div>
                    <div className="md:col-span-3">
                      <UILabel>{text.role}</UILabel>
                      <UIInput value={item.role} onChange={(e) => updateLabor(item.id, "role", e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                      <UILabel>{text.workers}</UILabel>
                      <UIInput type="number" value={item.workers} onChange={(e) => updateLabor(item.id, "workers", e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                      <UILabel>{text.days}</UILabel>
                      <UIInput type="number" value={item.days} onChange={(e) => updateLabor(item.id, "days", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <UILabel>{text.hoursPerDay}</UILabel>
                      <UIInput type="number" value={item.hoursPerDay} onChange={(e) => updateLabor(item.id, "hoursPerDay", e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                      <UILabel>{text.hourlyRate}</UILabel>
                      <UIInput type="number" value={item.hourlyRate} onChange={(e) => updateLabor(item.id, "hourlyRate", e.target.value)} />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <UIButton type="button" theme={theme} variant="softDanger" size="icon" onClick={() => setForm((prev) => ({ ...prev, labor: prev.labor.length > 1 ? prev.labor.filter((row) => row.id !== item.id) : prev.labor }))} title={text.remove}>
                        <Trash2 className="h-4 w-4" />
                      </UIButton>
                    </div>
                    <div className="md:col-span-12">
                      <div className={`rounded-2xl border border-dashed px-4 py-3 text-sm font-bold ${theme === "dark" ? "border-slate-700 text-slate-200" : "border-slate-300 text-slate-700"}`}>
                        {text.total}: {formatMoney(lineTotal)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </PanelCard>

          <PanelCard>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <SectionHeading title={text.extras} subtitle={text.extrasSubtotal} />
              <UIButton type="button" theme={theme} onClick={() => setForm((prev) => ({ ...prev, extras: [...prev.extras, makeExtraRow()] }))} className="gap-2 self-start md:self-auto">
                <Plus className="h-4 w-4" />
                {text.addExtra}
              </UIButton>
            </div>

            <div className="space-y-4">
              {form.extras.map((item) => (
                <div key={item.id} className={`grid gap-3 rounded-2xl border p-4 md:grid-cols-12 ${theme === "dark" ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <div className="md:col-span-8">
                    <UILabel>{text.extraTitle}</UILabel>
                    <UIInput value={item.title} onChange={(e) => updateExtra(item.id, "title", e.target.value)} />
                  </div>
                  <div className="md:col-span-3">
                    <UILabel>{text.amount}</UILabel>
                    <UIInput type="number" value={item.amount} onChange={(e) => updateExtra(item.id, "amount", e.target.value)} />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <UIButton type="button" theme={theme} variant="softDanger" size="icon" onClick={() => setForm((prev) => ({ ...prev, extras: prev.extras.length > 1 ? prev.extras.filter((row) => row.id !== item.id) : prev.extras }))} title={text.remove}>
                      <Trash2 className="h-4 w-4" />
                    </UIButton>
                  </div>
                </div>
              ))}
            </div>
          </PanelCard>
        </div>

        <div className="space-y-6">
          <PanelCard>
            <SectionHeading title={text.summary} subtitle={text.finalPrice} />

            <div className="mt-5 space-y-3">
              {[
                [text.materialsSubtotal, statItems.materialsSubtotal, Package],
                [text.laborSubtotal, statItems.laborSubtotal, Hammer],
                [text.extrasSubtotal, statItems.extrasSubtotal, Receipt],
                [text.subtotal, statItems.subtotal, Layers3],
                [text.overheadAmount, statItems.overheadAmount, Building2],
                [text.profitAmount, statItems.profitAmount, BriefcaseBusiness],
                [text.netOffer, statItems.netOffer, Calculator],
                [text.vatAmount, statItems.vatAmount, UserRound],
                [text.grandTotal, statItems.grandTotal, Euro],
              ].map(([label, value, Icon], index) => (
                <div key={`${label}-${index}`} className={`flex items-center justify-between rounded-2xl px-4 py-3 ${theme === "dark" ? "bg-slate-900" : "bg-slate-50"}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className={`text-sm font-medium ${currentTheme.muted}`}>{label}</span>
                  </div>
                  <span className={`text-sm font-extrabold ${label === text.grandTotal ? "text-blue-600" : theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    {formatMoney(value)}
                  </span>
                </div>
              ))}

              <div className={`rounded-2xl border px-4 py-4 ${theme === "dark" ? "border-slate-800 bg-slate-950/50" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={`text-sm font-semibold ${currentTheme.muted}`}>{text.pricePerM2}</span>
                  <span className={`text-base font-black ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    {formatMoney(statItems.pricePerM2)}
                  </span>
                </div>
              </div>

              <UIButton type="button" theme={theme} variant="primary" size="lg" className="w-full" onClick={handleSave} disabled={saving}>
                <Calculator className="h-4 w-4" />
                {saving ? text.saving : text.save}
              </UIButton>
            </div>
          </PanelCard>

          <PanelCard>
            <SectionHeading title={text.savedList} subtitle={`${savedCalculations.length}`} />

            <div className="mt-5 space-y-3">
              {savedCalculations.length === 0 ? (
                <div className={`rounded-2xl border border-dashed p-5 text-sm ${theme === "dark" ? "border-slate-700 text-slate-400" : "border-slate-300 text-slate-500"}`}>
                  {text.noSaved}
                </div>
              ) : (
                savedCalculations.slice(0, 12).map((item) => (
                  <div key={item.id} className={`rounded-2xl border p-4 ${theme === "dark" ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className={`truncate text-sm font-black ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                          {item.title || "-"}
                        </h3>
                        <p className={`mt-1 text-xs ${currentTheme.muted}`}>
                          {(item.projectName || text.noProjectName)} • {(item.clientName || text.noClientName)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {text.createdAt}: {formatCreatedAt(item.createdAt)}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-blue-500/10 px-3 py-2 text-sm font-black text-blue-600">
                        {formatMoney(item?.totals?.grandTotal || 0)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </PanelCard>
        </div>
      </div>
    </PageShell>
  );
}
